/**
 * Scoped recovery for a lazy route — keeps the shell (nav) mounted when a
 * page chunk throws, instead of dropping into the full-app CrashFallback.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { CrashFallback } from '@/components/CrashFallback';
import { isChunkLoadError, tryRecoverFromStaleChunk } from '@/lib/lazy-retry';
import * as Sentry from '@sentry/react';

type Props = { children: ReactNode };
type State = { error: Error | null };

export class RouteErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    if (isChunkLoadError(error) && tryRecoverFromStaleChunk()) return;
    Sentry.captureException(error, {
      contexts: { react: { componentStack: info.componentStack ?? undefined } },
    });
  }

  render() {
    if (this.state.error) {
      return (
        <CrashFallback
          compact
          onRetry={() => {
            this.setState({ error: null });
            window.location.reload();
          }}
        />
      );
    }
    return this.props.children;
  }
}
